import clsx from 'clsx';
import {
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  CardProps,
  Theme,
  useTheme,
} from '@material-ui/core';
import { Tooltip } from 'app/components/BasicTooltips/Tooltip';
import { SimpleList } from 'app/components/BasicList/StyledList';
import React, { useState } from 'react';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { defaultImgSrc } from 'app/components/AssetThumbImage';
import { Body } from '../Typography';
import { bookitColors } from 'styles/colors/bookitColors';
import styled from 'styled-components';
import { bookitShadows } from 'styles/shadows/bookitShadow';
// import useState from 'react-usestateref';

export type CardPosition = 'vertical' | 'horizontal';
export type CardBackground = 'white' | 'gray';
export type CardSize = 'small' | 'medium';
export type StatusType = 'info' | 'success' | 'warning' | 'error' | 'active';
export interface CardButton {
  key: React.Key | null | undefined;
  button: React.ReactNode;
}
export interface CardContentRow {
  key: React.Key | null | undefined;
  label: React.ReactNode;
  text: React.ReactNode;
}
export interface CardTopActions {
  key: React.Key | null | undefined;
  action: React.ReactNode;
}
export interface DemSize {
  width: number;
  height: number;
}
export interface BasicCardProps {
  position?: CardPosition;
  backStyle?: CardBackground;
  cardSize?: CardSize;
  mobileSize?: CardSize;
  useImage?: boolean;
  imageSrc?: string;
  showBullet?: boolean;
  useButtons?: boolean;
  useSelect?: boolean;
  selectVisible?: boolean;
  selected?: boolean;
  title?: React.ReactNode;
  disabled?: boolean;
  primaryAction?: () => void;
  checkBox?: React.ReactNode;
  topActions?: Array<CardTopActions>;
  hasTopActions?: boolean;
  buttons?: Array<CardButton>;
  cardContent?: React.ReactNode;
  cardContentRows?: Array<CardContentRow>;
  iconTitle?: React.ReactNode;
  status?: StatusType;
  maxContentHeight?: number;
  preventInnerClicks?: boolean;
  contentEditable?: boolean;
  loading?: boolean;
  info?: string;
}

export function BasicCard(props: BasicCardProps) {
  const {
    position,
    cardSize,
    mobileSize,
    useButtons,
    useImage,
    useSelect,
    showBullet,
    selected,
    title,
    imageSrc,
    disabled,
    primaryAction,
    checkBox,
    topActions,
    buttons,
    cardContent,
    cardContentRows,
    selectVisible,
    iconTitle,
    status,
    maxContentHeight,
    preventInnerClicks,
    contentEditable,
    hasTopActions,
    loading,
  } = props;
  const theme = useTheme();
  const [imgError, setImageError] = React.useState<boolean>(false);
  const imgSrc = React.useMemo(() => {
    return useImage
      ? imgError
        ? defaultImgSrc
        : imageSrc !== undefined
        ? imageSrc
        : defaultImgSrc
      : undefined;
  }, [imageSrc, imgError, useImage]);
  const [imageDimensions, setImageDimensions] = useState<DemSize | undefined>(
    undefined,
  );
  React.useEffect(() => {
    if (loading !== undefined) {
      if (loading === true && imageDimensions !== undefined) {
        setImageDimensions(undefined);
      }
    }
  }, [imageDimensions, loading]);
  const loadImage = (setImageDimensions, imageUrl) => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      setImageDimensions({
        height: img.height,
        width: img.width,
      });
      // console.debug('image loaded', img);
    };
    // img.onerror = err => {
    //   console.log('img error');
    //   console.error(err);
    // };
  };
  const backgroundSize = React.useMemo(() => {
    if (!!imageDimensions) {
      let horizontal = imageDimensions.width - imageDimensions.height > 0;
      if (imageDimensions.height === imageDimensions.width) {
        return 'contain';
      }
      if (position === 'vertical') {
        if (horizontal) {
          if (
            imageDimensions.height <
            (cardSize === 'medium' ? 180 : 150) / 1.8
          ) {
            return 'contain';
          } else {
            if (imageDimensions.width < imageDimensions.height * 1.33) {
              return 'contain';
            }
            return 'cover';
          }
        } else {
          return 'contain';
        }
      } else {
        if (!horizontal) {
          if (
            imageDimensions.height <
            (cardSize === 'medium' ? 180 : 150) / 1.8
          ) {
            return 'contain';
          } else {
            return 'cover';
          }
        } else {
          if (
            imageDimensions.width <
            (cardSize === 'medium' ? 180 : 150) / 1.8
          ) {
            return 'contain';
          } else {
            return 'cover';
          }
        }
      }
    }
    return undefined;
  }, [cardSize, imageDimensions, position]);
  const [showSelect, setShowSelect] = useState(selectVisible);
  React.useEffect(() => {
    let active =
      imgSrc !== undefined &&
      imageDimensions === undefined &&
      loading === false;
    if (active) {
      loadImage(setImageDimensions, imgSrc);
    }
    return () => {
      active = false;
      //setImageDimensions(undefined);
    };
  }, [imageDimensions, imgSrc, loading]);
  const showHeader = React.useMemo(() => {
    return !!title || !!iconTitle || (useSelect && showSelect) || hasTopActions;
  }, [iconTitle, showSelect, title, hasTopActions, useSelect]);
  // const headerHeight = React.useMemo(() => {
  //   return showHeader ? 25 : 0;
  // }, [showHeader]);
  // const imageHeight = React.useMemo(() => {
  //   return position === 'horizontal' || imgSrc === undefined
  //     ? 0
  //     : cardSize === 'medium'
  //     ? 180
  //     : 150;
  // }, [cardSize, imgSrc, position]);

  // const rowsHeight = 78;

  const contentHeight = React.useMemo(() => {
    let maxHeight = maxContentHeight || 60;
    return cardContent === undefined ? 0 : maxHeight;
  }, [cardContent, maxContentHeight]);
  // const otherHeight = React.useMemo(() => {
  //   //paddings and gaps
  //   return (
  //     (rowsHeight > 0 || maxContentHeight === undefined
  //       ? cardSize === 'medium'
  //         ? 16 * 2
  //         : 8 * 2
  //       : 6) + (useButtons ? 16 : 8)
  //   );
  // }, [cardSize, maxContentHeight, rowsHeight, useButtons]);

  // const buttonsHeight = React.useMemo(() => {
  //   return useButtons ? 32 : 0;
  // }, [useButtons]);
  const [mouseUp, setMouseUp] = React.useState<boolean>(false);
  // const [preventPrimary, setPreventPrimary] = React.useState<boolean>(false);
  // const cardHeight = React.useMemo(() => {
  //   return (
  //     headerHeight + rowsHeight + buttonsHeight + otherHeight + contentHeight
  //   );
  // }, [buttonsHeight, headerHeight, otherHeight, rowsHeight, contentHeight]);

  const trimTitleText = React.useMemo(() => {
    if (!!title && typeof title === 'string') {
      let trimmedTitle = title as string;
      let trimSize = cardSize === 'medium' ? 24 : 26;
      if (trimmedTitle.length > trimSize) {
        trimmedTitle = trimmedTitle.substring(0, trimSize).trim() + '...';
      }
      return trimmedTitle;
    }
    return title;
  }, [cardSize, title]);

  return (
    <StyledCard
      theme={theme}
      //style={{ height: `${cardHeight}px` }}
      className={clsx('card', {
        cardActive: status === 'active' || selected,
        cardInfo: status === 'info',
        cardSuccess: status === 'success',
        cardWarning: status === 'warning',
        cardError: status === 'error',
        position_vertical: position === 'vertical',
        position_horizontal: position === 'horizontal',
        cardSize_small: cardSize === 'small',
        cardSize_medium: cardSize === 'medium',
      })}
    >
      <CardActionArea
        className={clsx('cardActionArea', {
          position_vertical: position === 'vertical',
          position_horizontal: position === 'horizontal',
          disabled: disabled && !contentEditable,
        })}
        disabled={disabled && !contentEditable}
        component="span"
        onMouseEnter={() => {
          if (useSelect) {
            setShowSelect(true);
          }
        }}
        onMouseLeave={() => {
          if (useSelect) setShowSelect(selectVisible);
        }}
        onMouseUp={event => {
          if ('type' in event.target && event.target.type === 'checkbox') {
            event.preventDefault();
            event.stopPropagation();
          } else if (
            'className' in event.target &&
            event.target.className !== undefined &&
            event.target.className !== null &&
            typeof event.target.className === 'string' &&
            event.target.className.indexOf('MuiCardMedia-root') > -1
          ) {
            setMouseUp(true);
            if (!!primaryAction) {
              primaryAction();
            }
          } else if (
            ('className' in event.target &&
              event.target.className !== undefined &&
              event.target.className !== null &&
              typeof event.target.className === 'string' &&
              event.target.className.indexOf('MuiLink-root') > -1) ||
            event.target?.parentElement?.nodeName === 'A'
          ) {
            setMouseUp(true);
          } else {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onClick={() => {
          if (mouseUp) {
            setMouseUp(false);
            return;
          }
          if (!!primaryAction) {
            primaryAction();
          }
        }}
      >
        {imgSrc !== undefined && (
          <ImageContainer
            theme={theme}
            className={clsx('imgContainer', {
              position_vertical: position === 'vertical',
              position_horizontal: position === 'horizontal',
              cardSize_small: cardSize === 'small',
              cardSize_medium: cardSize === 'medium',
              mobileSizeMedium: mobileSize === 'medium',
              mobileSizeSmall: mobileSize === 'small',
            })}
          >
            <div
              className={clsx('frame', {
                position_vertical: position === 'vertical',
                position_horizontal: position === 'horizontal',
                cardSize_small: cardSize === 'small',
                cardSize_medium: cardSize === 'medium',
                mobileSizeMedium: mobileSize === 'medium',
                mobileSizeSmall: mobileSize === 'small',
              })}
              // style={
              //   position === 'vertical' ? { height: imageHeight } : undefined
              // }
            >
              <CardMedia
                // style={
                //   position === 'vertical'
                //     ? { height: 0, paddingTop: imageHeight }
                //     : undefined
                // }
                style={
                  !!backgroundSize
                    ? { backgroundSize: backgroundSize }
                    : undefined
                }
                component="div"
                className={clsx('mediaStyles', {
                  position_vertical: position === 'vertical',
                  position_horizontal: position === 'horizontal',
                  cardSize_small: cardSize === 'small',
                  cardSize_medium: cardSize === 'medium',
                  mobileSizeMedium: mobileSize === 'medium',
                  mobileSizeSmall: mobileSize === 'small',
                })}
                image={imgSrc}
                onLoadedMetadata={e => {
                  console.log('img event', e);
                }}
                onLoad={e => {
                  console.log('img event', e);
                }}
                src=""
                onError={() => setImageError(true)}
              >
                {useSelect &&
                  (showSelect || selectVisible) &&
                  position === 'vertical' &&
                  !disabled && <div className="checkTop">{checkBox}</div>}
              </CardMedia>
            </div>
          </ImageContainer>
        )}
        <CardContent
          className={clsx('cardContent', {
            cardSize_small: cardSize === 'small',
            cardSize_medium: cardSize === 'medium',
          })}
          // style={{ maxHeight: cardHeight, height: 'auto' }}
        >
          {showHeader && (
            <div className={'cardTitle'}>
              <div className={'title'}>
                {!!iconTitle && iconTitle}
                {title !== undefined && (
                  <div className={'titleOverRule'}>
                    <Tooltip title={title as string}>
                      <span>
                        <Body
                          bold={true}
                          size={cardSize === 'medium' ? 'large' : 'small'}
                        >
                          <React.Fragment>{trimTitleText}</React.Fragment>
                        </Body>
                      </span>
                    </Tooltip>
                  </div>
                )}
              </div>
              <div className={'checkWithAction'}>
                {(imgSrc === undefined || position === 'horizontal') &&
                  useSelect &&
                  (showSelect || selectVisible) &&
                  !disabled && <React.Fragment>{checkBox}</React.Fragment>}
                {!!topActions &&
                  topActions.map(c => {
                    return (
                      <span key={c.key} onClick={e => e.stopPropagation()}>
                        <React.Fragment>{c.action}</React.Fragment>
                      </span>
                    );
                  })}
              </div>
            </div>
          )}
          {!!cardContent && (
            <div className={'content'} style={{ height: contentHeight }}>
              <React.Fragment>{cardContent}</React.Fragment>
            </div>
          )}
          {!!cardContentRows && cardContentRows.length > 0 && (
            <SimpleList
              className={clsx('cardContentRows', {
                position_horizontal: position === 'horizontal',
                mobileSizeMedium: mobileSize === 'medium',
                mobileSizeSmall: mobileSize === 'small',
              })}
            >
              {cardContentRows.map(c => {
                return (
                  <li key={`list-key-${c.key}`} className={'contentRow'}>
                    {showBullet && (
                      <Icon
                        icon="circle"
                        fontSize="6px"
                        color="primary"
                        style={{ paddingTop: '8px' }}
                      />
                    )}

                    <span
                      onClickCapture={e => {
                        if (preventInnerClicks) {
                          e.preventDefault();
                        }
                      }}
                      className={'rowOverRule'}
                    >
                      <Body size="small" className={'rowTitle'}>
                        <span>{c.label}</span>
                      </Body>
                      <Body size="small" className={'rowText'}>
                        <span>{c.text}</span>
                      </Body>
                    </span>
                  </li>
                );
              })}
            </SimpleList>
          )}

          {useButtons && !!buttons && (
            <CardActions
              className={'cardActions'}
              style={
                contentEditable
                  ? { pointerEvents: 'initial', cursor: 'pointer' }
                  : undefined
              }
            >
              <div className={'cardActionsButtons'}>
                {buttons.map(c => {
                  return (
                    <span key={c.key} onClick={e => e.stopPropagation()}>
                      <React.Fragment>{c.button}</React.Fragment>
                    </span>
                  );
                })}
              </div>
              {status !== undefined && (
                <div>
                  <Icon
                    icon={
                      status === 'active'
                        ? 'check'
                        : status === 'success'
                        ? 'check-double'
                        : status === 'info'
                        ? 'info'
                        : status === 'warning'
                        ? 'triangle-exclamation'
                        : 'xmark'
                    }
                    color={
                      status === 'active'
                        ? 'primary'
                        : status === 'success'
                        ? 'success'
                        : status === 'info'
                        ? 'info'
                        : status === 'warning'
                        ? 'warning'
                        : 'danger'
                    }
                    size="lg"
                  />
                </div>
              )}
            </CardActions>
          )}
        </CardContent>
      </CardActionArea>
    </StyledCard>
  );
}
export const ImageContainer = styled('div')(({ theme }) => ({
  '&.imgContainer': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    alignSelf: 'stretch',
    background: `${bookitColors.base.white}`,
    borderRadius: '12px 12px 0px 0px',
    '&.position_horizontal': {
      borderRadius: '12px 0px 0px 12px',
      '&.mobileSizeMedium, &.mobileSizeSmall': {
        padding: '16px 0px 16px 16px',
      },
    },
    '&.position_vertical': {
      '&.cardSize_medium': {
        height: '180px',
      },
      '&.cardSize_small': {
        height: '150px',
      },
    },
    '& .frame': {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      gap: '10px',
      flex: '1 0 0',
      alignSelf: 'stretch',
      borderRadius: '12px 12px 0px 0px',
      background: `${bookitColors.grayscale.graySelection}`,
      '&.position_horizontal': {
        height: 'auto',
        borderRadius: '12px 0px 0px 12px',
        '&.mobileSizeMedium': {
          alignItems: 'center',
          //padding: '16px 0px',
          borderRadius: '8px',
          height: '80px',
        },
        '&.mobileSizeSmall': {
          alignItems: 'center',
          //padding: '16px 0px',
          borderRadius: '8px',
          height: '64px',
        },
      },
      '& .mediaStyles': {
        imageRendering: '-webkit-optimize-contrast',
        backgroundSize: 'auto',
        width: '100%',
        backgroundBlendMode: 'multiply',
        minWidth: 'unset',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 15,
        // '&.position_vertical': {
        //   height: '100%',
        //   width: '100%',
        // },
        '& .checkTop': {
          position: 'absolute',
          top: 0,
          left: 0,
          padding: '5px',
          borderRadius: '8px',
        },
        '&.position_horizontal': {
          height: '100%',
          minWidth: '64px',
          '&.cardSize_small': {
            width: '150px',
            '&.mobileSizeMedium': {
              width: '80px',
              height: '80px',
            },
            '&.mobileSizeSmall': {
              width: '64px',
              height: '64px',
            },
          },
          '&.cardSize_medium': {
            width: '180px',
            '&.mobileSizeMedium': {
              width: '80px',
              height: '80px',
            },
            '&.mobileSizeSmall': {
              width: '64px',
              height: '64px',
            },
          },
        },
        '&.position_vertical': {
          '&.cardSize_small': {
            height: 0,
            paddingTop: 150,
          },
          '&.cardSize_medium': {
            height: 0,
            paddingTop: 180,
          },
        },
      },
    },
  },
}));
export const StyledCard = styled(
  ({
    theme,
    children,
    ...other
  }: {
    theme: Theme;
    children: React.ReactNode;
  } & CardProps) => <Card {...other}>{children}</Card>,
)({
  '&.card': {
    display: 'flex',
    flexDirection: 'column',
    //justifyContent: 'flex-start',
    justifyContent: 'center',
    alignItems: 'flex-start',
    borderRadius: '12px',
    padding: 0,
    flex: '1 0 0',
    border: `1px solid ${bookitColors.grayscale.grayBorders}`,
    boxShadow: 'unset',
    height: '100%',
    '&.cardActive': {
      boxShadow: bookitShadows.shadowS,
      border: `1px solid ${bookitColors.primary.components}`,
      background: bookitColors.primary.background,
      '&.cardInfo': {
        border: `1px solid ${bookitColors.primary.borders}`,
        background: bookitColors.primary.bgLight,
      },
      '&.cardSuccess': {
        border: `1px solid ${bookitColors.success.components}`,
        background: bookitColors.success.background,
      },
      '&.cardWarning': {
        border: `1px solid ${bookitColors.warning.components}`,
        background: bookitColors.warning.background,
      },
      '&.cardError': {
        border: `1px solid ${bookitColors.danger.components}`,
        background: bookitColors.danger.background,
      },
      // boxShadow: `2px 1px 1px -2px ${bookitColors.primary.components}, 0px 0px 1px 2px ${bookitColors.primary.components}, 0px 1px 1px 0px ${bookitColors.primary.components}`,
    },

    '& .cardActionArea': {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      alignSelf: 'stretch',
      borderRadius: '12px',
      height: '100%',
      '&.position_horizontal': {
        flexDirection: 'row',
      },
      //border: `1px solid ${bookitColors.grayscale.grayBorders}`,
      '&.disabled': {
        backgroundColor: `${bookitColors.grayscale.grayBorders}`,
      },
      '&:hover': {
        opacity: 1,
        boxShadow: bookitShadows.shadowS,
        // border: `1px solid ${bookitColors.primary.components}`,
        background: bookitColors.primary.background,
        '&.cardInfo': {
          //border: `1px solid ${bookitColors.primary.borders}`,
          background: bookitColors.primary.bgLight,
        },
        '&.cardSuccess': {
          //border: `1px solid ${bookitColors.success.components}`,
          background: bookitColors.success.background,
        },
        '&.cardWarning': {
          //border: `1px solid ${bookitColors.warning.components}`,
          background: bookitColors.warning.background,
        },
        '&.cardError': {
          //border: `1px solid ${bookitColors.danger.components}`,
          background: bookitColors.danger.background,
        },
      },
      '& .cardContent': {
        // height: props => props.cardHeight - props.imageHeight, //isMobile ? 'auto' : `${cardcurrentHeight - 84}px`,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 8,
        alignSelf: 'stretch',
        height: '100%',
        justifyContent: 'space-between',
        '&.cardSize_small': {
          padding: '8px 12px 12px 12px',
        },
        '&.cardSize_medium': {
          padding: '16',
        },
        '& .cardTitle': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '25px',
          '& .title': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 8,
            '& .titleOverRule': {
              display: 'inline-block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '26ch',
              // [theme.breakpoints.down('sm')]: {
              //   maxWidth: '29ch',
              // },
            },
          },
          '& .checkWithAction': {
            display: 'flex',
            flexDirection: 'row',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'flex-start',
          },
        },
        '& .content': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: 0,
          margin: 0,
          gap: '8px',
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'clip',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            boxShadow: `inset 0 0 2px ${bookitColors.grayscale.grayBorders}`,
            borderRadius: '6px',
            backgroundColor: bookitColors.grayscale.grayBorders,
          },
        },
        '& .cardContentRows': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: 0,
          margin: 0,
          gap: '8px',
          flexGrow: 2,
          overflowY: 'clip',
          overflowX: 'clip',
          minHeight: '78px',
          height: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            boxShadow: `inset 0 0 2px ${bookitColors.grayscale.grayBorders}`,
            borderRadius: '6px',
            backgroundColor: bookitColors.grayscale.grayBorders,
          },
          '&.position_horizontal': {
            '& .contentRow': {
              '& .rowOverRule': {
                '& .rowTitle': {
                  maxWidth: '14ch',
                },
                // '& .rowText': {
                //   maxWidth: '14ch',
                // },
              },
            },
            '&.mobileSizeMedium, &.mobileSizeSmall': {
              '& .contentRow': {
                '& .rowOverRule': {
                  '& .rowTitle': {
                    maxWidth: '12ch',
                  },
                  // '& .rowText': {
                  //   maxWidth: '12ch',
                  // },
                },
              },
            },
          },
          '& .contentRow': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            padding: 0,
            gap: 8,
            height: 'auto',
            //height: '20px',
            // width: '100%',
            '& .rowOverRule': {
              display: 'flex',
              width: '100%',
              alignItems: 'flex-start',
              // whiteSpace: 'pre',
              // overflow: 'hidden',
              // textOverflow: 'ellipsis',
              // maxWidth: '36ch',
              '& .rowTitle': {
                color: bookitColors.grayscale.graySubtitles,
                paddingRight: 8,
                display: 'inline',
                whiteSpace: 'pre',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '16ch',
              },
              '& .rowText': {
                color: bookitColors.grayscale.blackRegular,
                display: 'inline',
                whiteSpace: 'pre',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                //maxWidth: '18ch',
                '& > *': {
                  display: 'initial',
                },
              },
            },
          },
        },
        '& .cardActions': {
          justifyContent: 'space-between',
          padding: '0px',
          gap: 8,
          width: '100%',
          '& .cardActionsButtons': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            justifyContent: 'flex-start',
            width: '100%',
          },
        },
      },
    },
  },
});

export const BasicCardWithInfo = (props: BasicCardProps) => {
  const { info, ...other } = props;
  return info ? (
    <Tooltip title={info} PopperProps={{ disablePortal: true }}>
      <span>
        <BasicCard {...other} />
      </span>
    </Tooltip>
  ) : (
    <BasicCard {...other} />
  );
};
